import { useState, useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { aiConfigService } from '../../../services/aiConfigService';
import type { Component } from '../../../services/aiConfigService';
import { buildComponentTree, type TreeNode } from '../utils/treeBuilder';
import type { ComponentStatistics } from '../config';

export const useComponent = () => {
  const [components, setComponents] = useState<Component[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Component | null>(null);
  const [formRef, setFormRef] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  const loadComponents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiConfigService.getComponentList();
      // 确保数据中没有 children 属性，避免表格显示树形结构
      const flatData = (Array.isArray(data) ? data : []).map((item: any) => {
        if (item && typeof item === 'object') {
          const { children, ...rest } = item;
          return rest;
        }
        return item;
      });
      setComponents(flatData);
      setTreeData(buildComponentTree(data));
    } catch (err: any) {
      setError(err.message || '加载组件列表失败');
      message.error(err.message || '加载组件列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTreeData = useCallback(async () => {
    try {
      const data = await aiConfigService.getComponentList();
      setTreeData(buildComponentTree(data));
    } catch (err: any) {
      message.error(`加载树形数据失败: ${(err as Error).message}`);
    }
  }, []);

  const handleSelect = useCallback((keys: React.Key[]) => {
    if (keys.length === 0) {
      setSelectedNode(null);
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(keys);
  }, []);

  const handleOpenModal = useCallback((record?: Component | null) => {
    setEditingRecord(record ?? null);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setEditingRecord(null);
    formRef?.resetFields?.();
  }, [formRef]);

  const handleSave = useCallback(async () => {
    if (!formRef) return;
    try {
      const values = await formRef.validateFields();
      
      // 处理来源字段：解析 serviceName:pageId:componentId
      const sourceParts = values.source?.split(':') || [];
      const serviceName = sourceParts[0] || '';
      const pageId = sourceParts[1] || '';
      const componentId = sourceParts[2] || '';
      
      // 处理思考方式关联数据（只保留思考方式ID数组）
      const thinkingModes = Array.isArray(values.thinkingModes)
        ? values.thinkingModes.map((thinkingModeId: string) => ({
            thinkingModeId,
          }))
        : [];

      const payload = {
        ...editingRecord,
        ...values,
        serviceName,
        pageId,
        componentId,
        thinkingModes,
      };
      
      // 删除 source 字段（不保存到后端）
      delete payload.source;
      
      // TODO: 使用 mock 数据，后续替换为真实 API
      console.log('保存组件（Mock）:', payload);
      // await aiConfigService.upsertComponent(payload);
      message.success('保存成功（Mock 模式）');
      handleCloseModal();
      loadComponents();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  }, [formRef, editingRecord, handleCloseModal, loadComponents]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        // TODO: 调用删除 API
        await aiConfigService.getComponentList();
        message.success('删除成功');
        loadComponents();
      } catch (err: any) {
        message.error(err?.message || '删除失败');
      }
    },
    [loadComponents]
  );

  const handleToggleActive = useCallback(
    async (record: Component) => {
      try {
        const payload = {
          ...record,
          isActive: !record.isActive,
        };
        // TODO: 使用 mock 数据，后续替换为真实 API
        console.log('切换组件状态（Mock）:', payload);
        // await aiConfigService.upsertComponent(payload);
        message.success(`${payload.isActive ? '启用' : '禁用'}成功（Mock 模式）`);
        loadComponents();
      } catch (err: any) {
        message.error(err?.message || '操作失败');
      }
    },
    [loadComponents]
  );

  // 计算统计信息
  const statistics = useMemo<ComponentStatistics>(() => {
    const total = components.length;
    const active = components.filter((c) => c.isActive !== false).length;
    const inactive = total - active;
    const chart = components.filter((c) => c.type === 'chart').length;
    const table = components.filter((c) => c.type === 'table').length;
    const form = components.filter((c) => c.type === 'form').length;
    const dashboard = components.filter((c) => c.type === 'dashboard').length;

    return {
      totalComponents: total,
      activeComponents: active,
      inactiveComponents: inactive,
      chartComponents: chart,
      tableComponents: table,
      formComponents: form,
      dashboardComponents: dashboard,
    };
  }, [components]);

  useEffect(() => {
    loadComponents();
  }, [loadComponents]);

  return {
    components,
    treeData,
    loading,
    error,
    modalVisible,
    editingRecord,
    formRef,
    setFormRef,
    expandedKeys,
    selectedKeys,
    selectedNode,
    statistics,
    loadComponents,
    loadTreeData,
    handleSelect,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    handleToggleActive,
    setExpandedKeys,
  };
};
